import React from "react";

import { ReactLazyLoading } from "@/containers/ReactLoading";
import { useAppSelector } from "@/hooks";
import { sessionSelector } from "@/store/sessionReducer";

const SugDashboard = ReactLazyLoading(() => import("@/customers/sug/dashboard"));

const SkDashboard = ReactLazyLoading(() => import("@/customers/sk/dashboard"));

type DashboardComponentsType = {
  [customerIdentifier: string]: Partial<{
    [projectIdentifier: string]: React.ComponentType;
  }>;
};

const DASHBOARD_COMPONENTS: DashboardComponentsType = {
  "gju-demo": {
    demo: ReactLazyLoading(() => import("@/customers/gju-demo/dashboard")),
  },

  "sevenit-gmbh": {
    sales: ReactLazyLoading(() => import("@/customers/sevenit/dashboard/SalesDashboard")),
    "welcome-calls": ReactLazyLoading(() => import("@/customers/sevenit/dashboard/Welcome")),
    "upselling-6-2021": ReactLazyLoading(() => import("@/customers/sevenit/dashboard/Upselling621")),
    "upgrade-plan-b": ReactLazyLoading(() => import("@/customers/sevenit/dashboard/Outbound")),
    "implementation-calls": ReactLazyLoading(() => import("@/customers/sevenit/dashboard/Outbound")),
    retention: ReactLazyLoading(() => import("@/customers/sevenit/dashboard/Retention")),
    default: ReactLazyLoading(() => import("@/customers/sevenit/dashboard/Support")),
  },

  "sug-ag": {
    default: SugDashboard,
  },

  "sug-gmbh": {
    default: SugDashboard,
  },

  "sk-dienstleistungen": {
    default: SkDashboard,
  },

  "lr-health-beauty": {
    service: ReactLazyLoading(() => import("@/customers/lr-health-beauty/service/Dashboard")),
    "outbound-exit": ReactLazyLoading(() => import("@/customers/lr-health-beauty/outbound-exit/Dashboard")),
    "lr-uk": ReactLazyLoading(() => import("@/customers/lr-health-beauty/lr-uk/Dashboard")),
    "service-uk": ReactLazyLoading(() => import("@/customers/lr-health-beauty/uk-service/Dashboard")),
    vertrieb: ReactLazyLoading(() => import("@/customers/lr-health-beauty/vertrieb/Dashboard")),
  },

  "lr-france": {
    service: ReactLazyLoading(() => import("@/customers/lr-france/dashboard/Service")),
  },

  termitel: {
    bewerbermanagement: ReactLazyLoading(() => import("@/customers/termitel/dashboard/bewerbermanagement/Dashboard")),
    projektmanagement: ReactLazyLoading(() => import("@/customers/termitel/dashboard/projektmanagement/Dashboard")),
    vertrieb: ReactLazyLoading(() => import("@/customers/termitel/dashboard/Vertrieb")),
    default: ReactLazyLoading(() => import("@/customers/termitel/dashboard/verwaltung/Dashboard")),
  },

  proxytel: {
    default: ReactLazyLoading(() => import("@/customers/proxytel/vertrieb/Dashboard")),
  },

  smr: {
    default: ReactLazyLoading(() => import("@/customers/smr/dashboard")),
    "fini-resort-badenweiler": ReactLazyLoading(() => import("@/customers/smr/dashboard/FiniResort")),
  },

  "mercedes-benz": {
    "van-bestandsfahrzeuge": ReactLazyLoading(
      () => import("@/customers/mercedes-benz/Dashboard/van-bestandsfahrzeuge"),
    ),
    "paritaetischer-wohlfahrtsverband": ReactLazyLoading(
      () => import("@/customers/mercedes-benz/Dashboard/paritaetischer-wohlfahrtsverband"),
    ),
    selbach: ReactLazyLoading(() => import("@/customers/mercedes-benz/Dashboard/selbach")),
    "transporter-rheinland": ReactLazyLoading(
      () => import("@/customers/mercedes-benz/Dashboard/transporter-rheinland"),
    ),
  },

  zimmermann: {
    default: ReactLazyLoading(() => import("@/customers/zimmermann/Dashboard")),
  },

  demo: {
    default: ReactLazyLoading(() => import("@/customers/demo/dashboard")),
  },

  siwave: {
    default: ReactLazyLoading(() => import("@/customers/siwave/Dashboard")),
  },

  "allianz-vertretung-claus-buerger": {
    default: ReactLazyLoading(() => import("@/customers/allianz-vertretung-claus-buerger/Dashboard")),
  },

  quantiquest: {
    siegel: ReactLazyLoading(() => import("@/customers/quantiquest/Dashboard/siegel")),
    fahrschulen: ReactLazyLoading(() => import("@/customers/quantiquest/Dashboard/fahrschulen")),
    "top-berater": ReactLazyLoading(() => import("@/customers/quantiquest/Dashboard/top-berater")),
    default: ReactLazyLoading(() => import("@/customers/quantiquest/Dashboard/default")),
  },

  brinkmann: {
    default: ReactLazyLoading(() => import("@/customers/brinkmann/Dashboard")),
  },

  cityclean: {
    kundenrueckgewinnung: ReactLazyLoading(() => import("@/customers/cityclean/kundenrueckgewinnung/Dashboard")),
    neugruender: ReactLazyLoading(() => import("@/customers/cityclean/neugruender/Dashboard")),
    saisonkunden: ReactLazyLoading(() => import("@/customers/cityclean/saisonkunden/Dashboard")),
  },

  kesseboehmer: {
    default: ReactLazyLoading(() => import("@/customers/kesseboehmer/Dashboard")),
  },

  "wuestenrot-sascha-maurer": {
    default: ReactLazyLoading(() => import("@/customers/wuestenrot-sascha-maurer/Dashboard")),
  },

  "infinity-media": {
    "vertrieb-ch": ReactLazyLoading(() => import("@/customers/infinity-media/Dashboard/vertrieb_ch")),
    default: ReactLazyLoading(() => import("@/customers/infinity-media/Dashboard/vertrieb_de")),
  },
};

export default function SpecificComponent() {
  const { currentCustomer, currentProject } = useAppSelector(sessionSelector);

  const Component =
    DASHBOARD_COMPONENTS[currentCustomer.identifier]?.[currentProject.identifier] ||
    DASHBOARD_COMPONENTS[currentCustomer.identifier]?.default;

  if (!Component) {
    return null;
  }

  return <Component />;
}
